import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createQuotation,
  fetchAllProducts,
  fetchAllQuotations,
  fetchOneLead,
  fetchTotalLeadCount,
  getLeadAssignee,
  getQuotationNumber,
  handleFetchLeadQuotation,
  updateOneLeadData,
  updateQuotationApi,
} from "../../api/Lead.api";
import { handleLeadOrder } from "../../api/order.api";
import ILeadData, { IAddress, ILeadUser } from "../../models/LeadData.model";
import { IProduct } from "../../models/productData.model";
import { IQuotationData } from "../../models/quotationData.model";

interface LeadState {
  leads: any;
  quotations: any[];
  quotationNumber: string;
  product: { [key: string]: { count: number } };
  loading: boolean;
  error: string | null;
  orders: {};
  leadCount?: number;
  assignees: ILeadUser[];
  updateOutsideModal: boolean;
}

const initialState: LeadState = {
  assignees: [],
  leads: [],
  quotations: [],
  quotationNumber: "",
  product: {},
  loading: false,
  error: null,
  orders: {},
  leadCount: undefined,
  updateOutsideModal: false,
};

export const fetchLeadById = createAsyncThunk(
  "leads/fetchLeadById",
  async (leadId: string, { rejectWithValue }) => {
    try {
      const response = await fetchOneLead(leadId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch lead");
    }
  }
);

export const fetchLeadCount = createAsyncThunk(
  "leads/fetchLeadCount",
  async () => {
    try {
      const response = await fetchTotalLeadCount();
      return response;
    } catch (error: any) {
      return error.response?.data || "Failed to fetch lead";
    }
  }
);

export const getLeadAssigneeRoleWise = createAsyncThunk(
  "leads/getLeadAssignee",
  async () => {
    try {
      const response = await getLeadAssignee();
      return response.data;
    } catch (error: any) {
      return error.response?.data || "Failed to fetch lead Assignee";
    }
  }
);

export const updateLeadData = createAsyncThunk(
  "leads/updateLeadData",
  async (
    card: { _id: string; data: Partial<ILeadData> },
    { rejectWithValue }
  ) => {
    try {
      const response = await updateOneLeadData(card);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to update lead data"
      );
    }
  }
);

// Create Order
export const createLeadOrder = createAsyncThunk(
  "leads/createLeadOrder",
  async (
    {
      category,
      name,
      mobileNumber,
      email,
      shippingAddress,
      billingAddress,
      leadId,
      selectedGoods,
      gstin,
      quotationId,
      transportPrice,
      discountPrice,
      grandTotal,
    }: {
      category: string;
      name: string;
      mobileNumber: string;
      email: string;
      shippingAddress: IAddress;
      billingAddress: IAddress;
      leadId: string;
      selectedGoods: any;
      gstin: string;
      quotationId: string;
      transportPrice: number;
      discountPrice: number;
      grandTotal: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await handleLeadOrder(
        category,
        name,
        mobileNumber,
        email,
        shippingAddress,
        billingAddress,
        leadId,
        selectedGoods,
        gstin,
        quotationId,
        transportPrice,
        discountPrice,
        grandTotal
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to create order");
    }
  }
);

export const fetchProducts = createAsyncThunk(
  "leads/fetchProducts",
  async (_, { rejectWithValue }) => {
    try {
      const products = await fetchAllProducts();
      return products;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch products"
      );
    }
  }
);
export const fetchLeadQuotation = createAsyncThunk(
  "leads/fetchLeadQuotation",
  async (leadId: string, { rejectWithValue }) => {
    try {
      const leadQuotation = await handleFetchLeadQuotation(leadId);
      return leadQuotation;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch products"
      );
    }
  }
);

const leadSlice = createSlice({
  name: "leads",
  initialState,
  reducers: {
    setUpdateOutsideModal: (state) => {
      state.updateOutsideModal = true;
    },
    setUpdateOutsideModalFalse: (state) => {
      state.updateOutsideModal = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Update Lead Data
      .addCase(updateLeadData.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateLeadData.fulfilled, (state, action) => {
        state.loading = false;

        state.leads = action.payload;
      })
      .addCase(updateLeadData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Lead By ID
      .addCase(fetchLeadById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLeadById.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload;
      })
      .addCase(fetchLeadById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.product = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create Order
      .addCase(createLeadOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(createLeadOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;

        const { leadId, quotationId } = action.meta.arg;
        const leadIndex = state.quotations.findIndex(
          (lead: ILeadData) => lead._id === leadId
        );

        if (leadIndex !== -1) {
          const quotationIndex = state.quotations[
            leadIndex
          ].quotation.findIndex((q: IQuotationData) => q._id === quotationId);

          if (quotationIndex !== -1) {
            state.quotations[leadIndex].quotation[
              quotationIndex
            ].confirmedQuotation = true;
          }
        }
      })
      .addCase(createLeadOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchLeadQuotation.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(fetchLeadQuotation.fulfilled, (state, action) => {
        state.loading = false;
        state.quotations = action.payload;
      })
      .addCase(fetchLeadQuotation.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getLeadAssigneeRoleWise.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getLeadAssigneeRoleWise.fulfilled, (state, action) => {
        state.loading = false;
        state.assignees = action.payload;
      })
      .addCase(getLeadAssigneeRoleWise.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchLeadCount.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLeadCount.fulfilled, (state, action) => {
        state.loading = false;
        state.leadCount = action?.payload;
      })
      .addCase(fetchLeadCount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});
export const { setUpdateOutsideModal, setUpdateOutsideModalFalse } =
  leadSlice.actions;
export default leadSlice.reducer;
